/**
 * zone-board.ts — the zone rules every READ-ONLY ops board follows (Jeff, 11 October 2026,
 * confirmed 13 October 2026). Standard: Docs/Technical/admin-portal-ui-standards.md §
 * "Read-only ops boards are zone-agnostic".
 *
 *   1. Both zones are shown by default — the board fans out from this browser to every zone's
 *      admin API (`fanOutZones`, lib/zones.ts — the browser fan-out ADR-120 Amendment 2 permits;
 *      never a server-side aggregator).
 *   2. Every row carries its zone (the Zone column) — tagged here, by the fan-out, because no
 *      zone's API names itself.
 *   3. A per-zone legend is always on screen, computed over the UNFILTERED rows, so it reads the
 *      same whatever the filter shows.
 *   4. The zone filter defaults to "All zones" whatever the "Working in" zone is — the working
 *      zone governs edits, not reads.
 *   5. A zone that fails to load is a visible per-zone state ("Did not respond"), never an empty
 *      zone and never a hidden board — fail honestly.
 *
 * Pure helpers — no React, no I/O — so the rules are testable on their own. The Alerts board's
 * own rules (platform-wide alerts) stay in alerts-zones.ts; this module is the general case.
 */
import { ZONE_TABLE, ZONES, consoleZoneLabel, type ZoneResult } from './zones';

/** The filter value that keeps every zone. */
export const ALL_ZONES = 'all';

/** A row tagged by the fan-out with the zone it came from. */
export type Zoned<T> = T & { zone: string };

/**
 * Flatten a fan-out into one list, each row tagged with its zone code. A failed zone
 * contributes no rows here — its failure is reported by {@link zoneBoardLegend}, never absorbed.
 */
export function mergeZoneRows<T extends object>(results: ReadonlyArray<ZoneResult<T>>): Zoned<T>[] {
  const out: Zoned<T>[] = [];
  for (const r of results) for (const item of r.items) out.push({ ...item, zone: r.zone.code });
  return out;
}

/** Does a row survive the zone filter? `'all'` keeps everything. */
export const matchesZoneFilter = (row: { zone?: string }, filter: string): boolean =>
  filter === ALL_ZONES || row.zone === filter;

/**
 * Newest-first by an ISO timestamp field. Used to interleave two zones' rows into one board —
 * an interleave, never a sum: each row stays one zone's record and is labelled with its zone.
 * Rows with an unparseable time sort last.
 */
export function sortNewestFirst<T>(rows: T[], at: (row: T) => string | null | undefined): T[] {
  const t = (r: T) => {
    const v = Date.parse(at(r) ?? '');
    return Number.isNaN(v) ? -Infinity : v;
  };
  return [...rows].sort((a, b) => t(b) - t(a));
}

/**
 * One zone's legend state:
 *   - `ok`             — the zone answered;
 *   - `degraded`       — the zone answered, but said its read was degraded or unconfigured
 *                        (e.g. Tinybird token missing) — rows may be incomplete;
 *   - `failed`         — the zone did not respond;
 *   - `not-configured` — the platform has this zone but this console was built without its
 *                        address (ADR-123 Decision 2 — stated, never hidden).
 */
export type ZoneBoardState = 'ok' | 'degraded' | 'failed' | 'not-configured';

export interface ZoneBoardLegendRow {
  code: string;
  label: string;
  state: ZoneBoardState;
  /** Plain-language status for the legend's Status cell. */
  status: string;
  /** This zone's counts over the UNFILTERED rows, keyed by the board's own column keys. */
  counts: Record<string, number>;
}

const STATUS_TEXT: Record<ZoneBoardState, string> = {
  ok: 'Loaded',
  degraded: 'Partial read',
  failed: 'Did not respond',
  'not-configured': 'Not configured in this console',
};

/**
 * The per-zone legend: one row for every zone the PLATFORM has (ZONE_TABLE order), whether or
 * not it answered and whether or not this bundle can reach it.
 *
 * @param results  the fan-out's per-zone outcomes (null before the first load lands).
 * @param degraded zone code → reason, for a zone that answered but said its read was degraded or
 *                 unconfigured. Shown in the Status cell instead of "Loaded".
 * @param counts   the board's per-zone counts — computed by the caller over the UNFILTERED rows.
 */
export function zoneBoardLegend(
  results: ReadonlyArray<Pick<ZoneResult<unknown>, 'zone' | 'error'>> | null,
  {
    degraded = {},
    counts,
  }: {
    degraded?: Readonly<Record<string, string>>;
    counts: (code: string) => Record<string, number>;
  },
): ZoneBoardLegendRow[] {
  return ZONE_TABLE.map((z) => {
    const reachable = ZONES.some((c) => c.code === z.code);
    const result = results?.find((r) => r.zone.code === z.code);
    let state: ZoneBoardState;
    if (!reachable) state = 'not-configured';
    else if (result?.error) state = 'failed';
    else if (degraded[z.code]) state = 'degraded';
    else state = 'ok';
    return {
      code: z.code,
      label: consoleZoneLabel(z.code),
      state,
      status: state === 'degraded' ? degraded[z.code]! : STATUS_TEXT[state],
      counts: counts(z.code),
    };
  });
}

/** Count rows per zone code — the usual `counts` building block. */
export const countInZone = <T extends { zone?: string }>(
  rows: ReadonlyArray<T>,
  code: string,
  pred: (row: T) => boolean = () => true,
): number => rows.filter((r) => r.zone === code && pred(r)).length;
